import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import {
  MutationForm,
  ServerErrorFields,
  SuccessNotificationContext,
  useMutationForm,
} from '@dmc/ui';
import { ReactElement, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@customer/store/hooks';
import { closeSidePanel } from '@customer/store/slices/globalSlice';
import { z } from 'zod';
import {
  EditLoggedInvestigationModel,
  InvestigationModel,
  useEditInvestigationMutation,
} from '@customer/store/apis/investigationsApi';
import { CasFormCheckbox } from '@cas/components/inputs/cas-form-checkbox/CasFormCheckbox';
import { AssertTrue, IsBiDiCompatible } from '@cas/utils/typeHelpers';
import { getInvestigationBaseSchema } from '@customer/side-panels/manage-investigation/investigationBaseSchema';
import { MAX_INVESTIGATION_NAME } from '@customer/side-panels/manage-investigation/config';
import { ModelName } from '@customer/store/types';

export interface LoggedInvestigationFields {
  name: string;
  oneSecLog: boolean;
}

type InputFields = LoggedInvestigationFields;
type OutputFields = LoggedInvestigationFields;
type MutationArgs = EditLoggedInvestigationModel;

const serverErrorFields: ServerErrorFields<InputFields> = {
  name: 'n',
};

interface EditLoggedInvestigationFormProps {
  model: InvestigationModel;
}

export const EditLoggedInvestigationForm = ({
  model,
}: EditLoggedInvestigationFormProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [triggerMutation] = useEditInvestigationMutation();
  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const defaultValues = useMemo<InputFields>(() => {
    return {
      name: model.name ?? '', // Convert null names to empty strings.
      oneSecLog: model.oneSecLog,
    };
  }, [model.name, model.oneSecLog]);

  const getArgs = useCallback(
    (fieldValues: OutputFields): MutationArgs => {
      return {
        investigationId: model.id.toString(),
        n: fieldValues.name,
        oneSecLog: fieldValues.oneSecLog,
      };
    },
    [model.id],
  );

  const schema = useMemo(() => getInvestigationBaseSchema(t), [t]);

  // Use type assignment to compare expected output against schema shape during compilation.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type SchemaVerified = AssertTrue<IsBiDiCompatible<z.infer<typeof schema>, OutputFields>>;

  const successCallback = useCallback(() => {
    showSuccessMessage(t('Model.Edit.Success', { modelName: ModelName.Investigation }));
    dispatch(closeSidePanel());
  }, [dispatch, showSuccessMessage, t]);

  const serverErrorOverrides = useMemo<ServerErrorFields<InputFields>>(
    () => ({
      name: t('Validation.Maximum', { count: MAX_INVESTIGATION_NAME, unit: 'character' }),
    }),
    [t],
  );

  const { canSubmit, onSubmit, errorMessage, control } = useMutationForm({
    defaultValues,
    schema,
    triggerMutation,
    getArgs,
    successCallback,
    serverErrorFields,
    serverErrorOverrides,
    validateOnLoad: true,
  });

  return (
    <MutationForm
      control={control}
      errorTitle={t('Error')}
      submitLabel={t('SaveChanges')}
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
    >
      <CasFormInput
        control={control}
        name="name"
        label={t('Field.InvestigationName.Label')}
        helperText={t('Validation.Maximum', {
          count: MAX_INVESTIGATION_NAME,
          unit: 'character',
        })}
      />
      <CasFormCheckbox
        control={control}
        name="oneSecLog"
        label={t('Field.OneSecondLogs.Label')}
        // Disable the field if one second logs have already been enabled for this investigation.
        isReadonly={model.oneSecLog}
      />
    </MutationForm>
  );
};

export default EditLoggedInvestigationForm;
